import React from "react";

/**
 * TicketFormContainer Component
 * Wrapper component providing consistent layout and styling for ticket forms
 * 
 * @param {React.ReactNode} children - Form content
 * @param {string} className - Additional CSS classes
 * @param {Function} onSubmit - Form submission handler
 * @returns {JSX.Element}
 */
const TicketFormContainer = ({ children, className = "", onSubmit }) => {
  /**
   * Prevent default form submit and forward the event to `onSubmit`.
   *
   * @param {React.FormEvent<HTMLFormElement>} e Form submit event.
   * @returns {void}
   */
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit?.(e);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={`space-y-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}
    >
      {children}
    </form>
  );
};

/**
 * TicketFormSection Component
 * Groups related form fields with consistent spacing
 * 
 * @param {React.ReactNode} children - Section content
 * @param {string} className - Additional CSS classes
 * @returns {JSX.Element}
 */
export const TicketFormSection = ({ children, className = "" }) => {
  return (
    <div className={`space-y-4 pb-4 border-b border-gray-100 last:border-b-0 last:pb-0 ${className}`}>
      {children}
    </div>
  );
};

/**
 * TicketFormActions Component
 * Container for form action buttons (save, cancel, etc.)
 * 
 * @param {React.ReactNode} children - Action buttons
 * @param {string} className - Additional CSS classes
 * @returns {JSX.Element}
 */
export const TicketFormActions = ({ children, className = "" }) => {
  return (
    <div className={`flex justify-end gap-3 pt-4 border-t border-gray-200 ${className}`}>
      {children}
    </div>
  );
};

export default TicketFormContainer;
